// assets/kc-search.js — KitCellar search: get shoppers to the right club or category page in a tap.
//   - Quick panel in the search dialog ([data-kc-quick]): popular clubs and shop-by categories before typing,
//     then live club / league / category suggestions with kit shortcuts (Home, Away, Third ...) as they type.
//   - Enter goes to the top suggestion (unless a product result is selected with the arrow keys).
//   - Arriving on /search?q=...: if the query names a club, league or category (typos allowed), the page is replaced.
// Data: assets/kc-search-map.json (tools/build_search_map.ps1):
//   t { "<term>": [path, rank] }   c { path: { n name, r rank, q products, k [kit tags] } }   p popular   s shop-by
// Rank: club 3, league 2, product type 1; higher rank wins inside a phrase ("arsenal retro" is Arsenal).
(() => {
  const script = document.currentScript;
  const MAP_URL = script && script.dataset.map;
  if (!MAP_URL) return;

  const KIT_LABELS = {
    'kit-home': 'Home', 'kit-away': 'Away', 'kit-third': 'Third', 'kit-fourth': 'Fourth', 'kit-special': 'Special Edition',
    'kit-goalkeeper': 'Goalkeeper', 'kit-retro': 'Retro', 'kit-training': 'Training', 'kit-tracksuit': 'Tracksuits',
    'kit-outerwear': 'Outerwear', 'kit-leisure': 'Leisure',
  };
  const KIT_SHORTCUTS = 4; // kit links shown under the top suggestion

  /** @type {{ t: Record<string, [string, number]>, c: Record<string, {n: string, r: number, q: number, k: string[]}>, p: string[], s: string[] } | null} */
  let data = null;
  let loading;
  const load = () =>
    (loading ||= fetch(MAP_URL)
      .then((r) => (r.ok ? r.json() : null))
      .then((m) => (data = m && m.t ? m : null))
      .catch(() => null));
  load(); // the map is small; fetch it straight away so search never waits

  // Must match ConvertTo-SearchKey in tools/build_search_map.ps1
  const FOLD = { 'ø': 'o', 'ß': 'ss', 'æ': 'ae', 'œ': 'oe', 'ł': 'l', 'đ': 'd', 'ı': 'i' };
  const norm = (s) =>
    String(s || '')
      .toLowerCase()
      .replace(/[øßæœłđı]/g, (c) => FOLD[c])
      .replace(/&/g, ' and ')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();

  /** Best term found as a run of whole words in the query: highest rank, then longest. */
  function phrase(words, terms) {
    let best = null;
    for (let len = words.length; len >= 1; len--) {
      for (let i = 0; i + len <= words.length; i++) {
        const hit = terms[words.slice(i, i + len).join(' ')];
        if (hit && (!best || hit[1] > best.rank || (hit[1] === best.rank && len > best.len))) {
          best = { path: hit[0], rank: hit[1], len };
        }
      }
    }
    return best && best.path;
  }

  function distance(a, b, max) {
    if (Math.abs(a.length - b.length) > max) return max + 1;
    let prev = Array.from({ length: b.length + 1 }, (_, j) => j);
    for (let i = 1; i <= a.length; i++) {
      const cur = [i];
      let rowMin = i;
      for (let j = 1; j <= b.length; j++) {
        cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
        rowMin = Math.min(rowMin, cur[j]);
      }
      if (rowMin > max) return max + 1;
      prev = cur;
    }
    return prev[b.length];
  }

  /** Every page within typo distance of a query word (5+ letters; 2 edits allowed from 8 letters), closest first. */
  function fuzzy(words, terms) {
    const found = new Map();
    for (const w of words) {
      if (w.length < 5) continue;
      const max = w.length >= 8 ? 2 : 1;
      for (const key in terms) {
        if (key.length < 5 || key.includes(' ')) continue;
        const d = distance(w, key, max);
        if (d > max) continue;
        const path = terms[key][0];
        if (!found.has(path) || found.get(path) > d) found.set(path, d);
      }
    }
    return [...found.entries()].sort((a, b) => a[1] - b[1]).map(([path]) => path);
  }

  /** One page for a whole query: exact term, then a term inside the phrase, then (optionally) a typo match. */
  function resolve(query, allowTypos) {
    const q = norm(query);
    if (!q || !data) return null;
    if (data.t[q]) return data.t[q][0];
    const words = q.split(' ');
    const hit = phrase(words, data.t);
    if (hit) return hit;
    if (!allowTypos) return null;
    const typos = fuzzy(words, data.t);
    return typos.length === 1 ? typos[0] : null;
  }

  /** Pages to suggest while typing, best first: exact, phrase, then terms starting with what's typed, then typos. */
  function suggest(query, limit = 5) {
    const q = norm(query);
    if (!q || !data) return [];
    const scores = new Map();
    const add = (path, score) => {
      const page = data.c[path];
      if (!page) return;
      const s = score + page.r * 10 + Math.min(page.q, 500) / 1000;
      if (!scores.has(path) || scores.get(path) < s) scores.set(path, s);
    };
    const exact = data.t[q];
    if (exact) add(exact[0], 400);
    const inPhrase = phrase(q.split(' '), data.t);
    if (inPhrase) add(inPhrase, 300);
    if (q.length >= 2) {
      for (const key in data.t) {
        if (key.startsWith(q)) add(data.t[key][0], 200);
        else if (q.length >= 3 && key.includes(' ' + q)) add(data.t[key][0], 100);
      }
    }
    if (scores.size < limit) fuzzy(q.split(' '), data.t).forEach((path) => add(path, 50));
    return [...scores.entries()].sort((a, b) => b[1] - a[1]).slice(0, limit).map(([path]) => path);
  }

  // ---------------- quick panel ----------------
  const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const plural = (n) => `${n} product${n === 1 ? '' : 's'}`;

  function renderEmpty(panel) {
    if (!data) return;
    const chips = (paths) =>
      paths.filter((p) => data.c[p]).map((p) => `<a class="kc-quick__chip" href="${esc(p)}">${esc(data.c[p].n)}</a>`).join('');
    panel.innerHTML =
      `<p class="kc-quick__label">${esc(panel.dataset.popularLabel || 'Popular clubs')}</p>` +
      `<div class="kc-quick__chips">${chips(data.p)}</div>` +
      `<p class="kc-quick__label">${esc(panel.dataset.shopLabel || 'Shop by')}</p>` +
      `<div class="kc-quick__chips">${chips(data.s)}</div>`;
    panel.hidden = false;
  }

  function renderSuggestions(panel, paths) {
    if (!paths.length) { panel.hidden = true; panel.innerHTML = ''; return; }
    panel.innerHTML =
      `<ul class="kc-quick__list" role="list">` +
      paths
        .map((path, i) => {
          const page = data.c[path];
          const kits = i === 0 ? page.k.slice(0, KIT_SHORTCUTS) : [];
          return (
            `<li class="kc-quick__item${i === 0 ? ' is-top' : ''}">` +
            `<a class="kc-quick__link" href="${esc(path)}"><span class="kc-quick__name">${esc(page.n)}</span>` +
            `<span class="kc-quick__count">${plural(page.q)}</span></a>` +
            (kits.length
              ? `<div class="kc-quick__kits">${kits.map((k) => `<a class="kc-quick__chip" href="${esc(path)}/${k}">${KIT_LABELS[k] || k}</a>`).join('')}</div>`
              : '') +
            `</li>`
          );
        })
        .join('') +
      `</ul>`;
    panel.hidden = false;
  }

  function update(input) {
    const panel = input.form && input.form.querySelector('[data-kc-quick]');
    if (!panel) return;
    if (!data) { load().then(() => update(input)); return; }
    if (!input.value.trim()) renderEmpty(panel);
    else renderSuggestions(panel, suggest(input.value));
  }

  const isSearchInput = (el) => el instanceof HTMLInputElement && el.name === 'q';
  document.addEventListener('input', (e) => { if (isSearchInput(e.target)) update(e.target); });
  document.addEventListener('focusin', (e) => { if (isSearchInput(e.target)) update(e.target); });

  /** Where Enter should go for this search box: the whole query, else the top suggestion. */
  function enterTarget(input) {
    return resolve(input.value, false) || (data && input.value.trim() ? suggest(input.value, 1)[0] : null) || null;
  }

  // Enter in a search box. Capture phase, so this runs before the theme's own Enter handling; it steps aside
  // when the shopper has picked a product result with the arrow keys.
  document.addEventListener(
    'keydown',
    (e) => {
      if (e.key !== 'Enter' || e.isComposing || !isSearchInput(e.target)) return;
      const form = e.target.form;
      if (form && form.querySelector('[aria-selected="true"]')) return;
      const target = enterTarget(e.target);
      if (!target) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      location.assign(target);
    },
    true
  );

  const isSearchPath = (path) => /\/search\/?$/.test(path);

  // Plain form submits (search boxes without the dialog)
  document.addEventListener(
    'submit',
    (e) => {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      let action;
      try { action = new URL(form.getAttribute('action') || '', location.href); } catch { return; }
      if (!isSearchPath(action.pathname)) return;
      const input = form.querySelector('input[name="q"]');
      const target = input && resolve(input.value, false);
      if (!target) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      location.assign(target);
    },
    true
  );

  // Search results page: go to the category, unless the shopper is paging, sorting or filtering results
  if (isSearchPath(location.pathname)) {
    const params = new URLSearchParams(location.search);
    const q = (params.get('q') || '').trim();
    // Shopify's own tracking (_pos, _sid ...) and theme preview parameters don't count
    const extra = [...params.keys()].filter(
      (k) => !['q', 'options[prefix]', 'type', 'preview_theme_id'].includes(k) && !k.startsWith('_')
    );
    if (q && !extra.length) {
      load().then(() => {
        const target = resolve(q, true);
        if (target) location.replace(target);
      });
    }
  }
})();
